import {
  EXISTING_DISCOUNT_RULE,
  RENEWAL_DISCOUNT_CALCULATION,
  formatParisDate,
  formatParisDateTime,
  type OperationDetail,
  type RenewalDiscountDetail,
  type RenewalDiscountRun,
} from '@flux/shared';
import { AlertTriangle } from 'lucide-react';
import { useId, type JSX } from 'react';
import { Button } from '@/components/ui/button.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { formatCount, formatCountLabel, formatEuros } from '../subscriptions/money.js';

/** Recharge : ≈ 1 requête/s, ≈ 3 requêtes par client (création du code, application, relecture). */
export const SECONDS_PER_CUSTOMER = 3;

/** Dernier aperçu abouti, s'il y en a un. */
export function lastPreviewRun(config: RenewalDiscountDetail): RenewalDiscountRun | null {
  return config.lastRuns.find((run) => run.kind === 'preview' && run.status === 'succeeded' && run.preview !== null) ?? null;
}

export function lastPreview(config: RenewalDiscountDetail) {
  return lastPreviewRun(config)?.preview ?? null;
}

/**
 * Aperçu dont les réglages ont été enregistrés **après son démarrage** (le
 * worker lit les réglages au démarrage : un enregistrement pendant le calcul
 * le rend aussi périmé). Seulement en brouillon : l'activation met aussi à jour
 * `updatedAt`, et les réglages qui comptent pour l'aperçu sont figés ensuite.
 */
export function isPreviewStale(detail: OperationDetail, config: RenewalDiscountDetail, run: RenewalDiscountRun): boolean {
  if (config.state !== 'draft') {
    return false;
  }
  return new Date(detail.updatedAt).getTime() > new Date(run.startedAt).getTime();
}

export function durationLabel(customers: number): string {
  const minutes = Math.max(1, Math.round((customers * SECONDS_PER_CUSTOMER) / 60));
  return minutes < 60 ? `environ ${String(minutes)} min` : `environ ${String(Math.round(minutes / 6) / 10).replace('.', ',')} h`;
}

export interface RenewalActivateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: OperationDetail;
  config: RenewalDiscountDetail;
  isConfirming: boolean;
  /** Échec de l'activation, affiché dans le dialogue resté ouvert. */
  error?: string | null | undefined;
  onConfirm: () => void;
  /** Aperçu périmé : relance le calcul (le dialogue se ferme). */
  onRecomputePreview: () => void;
}

/**
 * Confirmation renforcée de l'activation (`07` §6.3) : rappel de ce qui va
 * être posé chez Recharge, des chiffres du dernier aperçu (date, péremption)
 * et de la durée de la passe initiale ; saisie du taux le plus haut à partir
 * de 30 %. Le dialogue ne se ferme pas pendant l'activation.
 */
export function RenewalActivateDialog({
  open,
  onOpenChange,
  isConfirming,
  ...props
}: RenewalActivateDialogProps): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isConfirming) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <ActivateDialogBody
          {...props}
          isConfirming={isConfirming}
          onCancel={() => {
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

type ActivateDialogBodyProps = Omit<RenewalActivateDialogProps, 'open' | 'onOpenChange'> & { onCancel: () => void };

/** Monté à chaque ouverture : le taux saisi ne repart à vide qu'à une fermeture effective. */
function ActivateDialogBody({
  detail,
  config,
  isConfirming,
  error = null,
  onConfirm,
  onRecomputePreview,
  onCancel,
}: ActivateDialogBodyProps): JSX.Element {
  const errorId = useId();
  const run = lastPreviewRun(config);
  const preview = run?.preview ?? null;
  const stale = run !== null && isPreviewStale(detail, config, run);
  const canConfirm = !isConfirming;

  return (
    <form
      className="grid gap-5"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (canConfirm) {
          onConfirm();
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>Activer « {detail.name} » ?</DialogTitle>
        <DialogDescription>
          La réduction sera ajoutée sur tous les renouvellements déjà prévus par Recharge (30 jours). Par la suite, dès qu’un
          renouvellement sera prévu par Recharge dans la période de l’opération, la réduction sera ajoutée au renouvellement
          automatiquement.
        </DialogDescription>
      </DialogHeader>
      <DescriptionList>
        <DescriptionItem label="Renouvellements visés">
          <span className="tabular-nums">
            du {formatParisDate(new Date(detail.period.start))}
            {detail.period.end === null ? '' : ` au ${formatParisDate(new Date(detail.period.end))}`}
          </span>
        </DescriptionItem>
        <DescriptionItem label="Paliers">
          <ul className="flex flex-col gap-0.5">
            {config.tiers.map((tier) => (
              <li key={tier.minProducts} className="tabular-nums">
                {`${formatCountLabel(tier.minProducts, 'produit', 'produits')} ou plus : ${String(tier.percent).replace('.', ',')} %`}
              </li>
            ))}
          </ul>
        </DescriptionItem>
        <DescriptionItem label="Calcul">{RENEWAL_DISCOUNT_CALCULATION[config.calculation].label}</DescriptionItem>
        <DescriptionItem label="Code existant">{EXISTING_DISCOUNT_RULE[config.existingDiscountRule].label}</DescriptionItem>
        <DescriptionItem label="Clients">
          {config.includeNewSubscribers ? 'Tous, nouveaux abonnements compris' : 'Abonnements existants à la date d’activation'}
          {config.excludeLegacy ? ', remise legacy exclue' : ''}
        </DescriptionItem>
        {run === null || preview === null ? (
          <DescriptionItem label="Aperçu">
            <span className="text-muted-foreground">Aucun aperçu calculé : le nombre de clients et le montant ne sont pas connus.</span>
          </DescriptionItem>
        ) : (
          <>
            <DescriptionItem label="Aperçu">
              <span className="tabular-nums">{`Aperçu du ${formatParisDateTime(new Date(run.finishedAt ?? run.startedAt))}`}</span>
            </DescriptionItem>
            <DescriptionItem label="Clients concernés">
              <span className="tabular-nums">
                {`${formatCount(preview.customersWithCharge)} avec un renouvellement déjà prévu, ${formatCount(
                  preview.notGeneratedCustomers,
                )} à venir`}
              </span>
            </DescriptionItem>
            <DescriptionItem label="Montant estimé">
              <span className="tabular-nums">{formatEuros(preview.totalDiscount) ?? '—'}</span>
            </DescriptionItem>
            <DescriptionItem label="Durée de la passe">{durationLabel(preview.customersWithCharge)}</DescriptionItem>
          </>
        )}
      </DescriptionList>
      {stale ? (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2 rounded-md border border-status-orange-border bg-status-orange-bg px-3 py-2 text-sm text-status-orange-fg">
          <p className="flex min-w-0 flex-1 items-start gap-2">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Les réglages ont changé depuis cet aperçu : ses chiffres peuvent être faux.
          </p>
          <Button type="button" variant="outline" size="sm" disabled={isConfirming} onClick={onRecomputePreview}>
            Recalculer l’aperçu
          </Button>
        </div>
      ) : null}
      {error === null ? null : (
        <p
          id={errorId}
          role="alert"
          className="rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg"
        >
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isConfirming}>
          Annuler
        </Button>
        <Button
          type="submit"
          isLoading={isConfirming}
          disabled={!canConfirm}
          aria-describedby={error === null ? undefined : errorId}
        >
          Activer l’opération
        </Button>
      </DialogFooter>
    </form>
  );
}
